import { parsePrice } from './utils';

const ROOT_SELECTOR = '.js-sticky-cart-flyout';
const BUY_BOX_FORM_SELECTOR = 'form[name="form_warenkorb"]';
const BUY_BOX_TRIGGER_SELECTOR = '[data-sticky-cart-flyout-trigger]';

const HEADER_SELECTOR = '.header';
const APP_INSTALL_BANNER_SELECTOR = '#apn-app-install';
const CLASS_HEADROOM_UNPINNED = 'headroom--unpinned';

const CLASS_VISIBLE = 'sticky-cart-flyout--visible';
const CLASS_NO_TRANSITION = 'sticky-cart-flyout--no-transition';

/**
 * Show/hide the sticky cart flyout.
 *
 * @param {HTMLElement} flyout
 * @param {boolean} visible
 * @param {boolean} animate
 */
function setFlyoutVisible(flyout, visible, animate = true) {
  if (!animate) {
    flyout.classList.add(CLASS_NO_TRANSITION);
  }

  flyout.classList.toggle(CLASS_VISIBLE, visible);
  flyout.setAttribute('aria-hidden', visible ? 'false' : 'true');
  flyout.inert = !visible;

  if (!animate) {
    flyout.getBoundingClientRect();
    flyout.classList.remove(CLASS_NO_TRANSITION);
  }
}

/**
 * Calculates the vertical pixel height of sticky elements currently occluding
 * the top of the viewport (e.g., sticky headers and app install banners).
 *
 * Used to shrink the IntersectionObserver root viewport so elements hidden
 * UNDER sticky overlays are treated as out-of-view (e.g. underneath the header).
 *
 * @returns {number}
 */
function getTopOcclusionHeight() {
  let height = 0;
  const header = document.querySelector(HEADER_SELECTOR);

  // The header only covers content while it is fixed,
  // and only when not translated off screen by headroom.
  if (header && getComputedStyle(header).position === 'fixed' && !header.classList.contains(CLASS_HEADROOM_UNPINNED)) {
    height = header.getBoundingClientRect().height;
  }

  // The app install banner is a sibling of the header, not a child.
  const appInstallBanner = document.querySelector(APP_INSTALL_BANNER_SELECTOR);

  if (appInstallBanner) {
    // Uses `bottom` coordinate because the banner might be partially scrolled off-screen
    // or translated upward (yielding a negative/zero bottom position when hidden/dismissed).
    height = Math.max(height, appInstallBanner.getBoundingClientRect().bottom);
  }

  return Math.max(0, Math.round(height));
}

/**
 * Notify when the overlaying elements at the top of the viewport change, i.e.
 * when headroom pins/unpins the header, when the header or banner is resized
 * or when the viewport changes.
 *
 * @param {() => void} onChange
 */
function watchTopOcclusion(onChange) {
  const header = document.querySelector(HEADER_SELECTOR);
  const appInstallBanner = document.querySelector(APP_INSTALL_BANNER_SELECTOR);
  const elements = [header, appInstallBanner].filter(Boolean);

  if (elements.length > 0) {
    const classObserver = new MutationObserver(onChange);

    for (const element of elements) {
      classObserver.observe(element, { attributes: true, attributeFilter: ['class'] });
    }

    // Catches size changes without a window resize, e.g. a dismissed announcement bar
    if (typeof ResizeObserver === 'function') {
      const sizeObserver = new ResizeObserver(onChange);

      for (const element of elements) {
        sizeObserver.observe(element);
      }
    }
  }

  window.addEventListener('resize', onChange);
}

/**
 * Controls the visibility of the sticky cart flyout based on whether the
 * primary buy box action is visible in the unoccluded viewport area.
 *
 * @param {HTMLElement} flyout
 */
function initVisibilityObserver(flyout) {
  const buyBoxForm = document.querySelector(BUY_BOX_FORM_SELECTOR);
  const trigger = buyBoxForm?.querySelector(BUY_BOX_TRIGGER_SELECTOR);
  const observedElement = trigger || buyBoxForm;

  if (!observedElement) {
    return;
  }

  let isFirstUpdate = true;
  let showTimeout;
  let observer;
  let occlusionHeight;

  const observerCallback = (entries) => {
    for (const entry of entries) {
      window.clearTimeout(showTimeout);

      if (entry.isIntersecting) {
        // The main button came into view -> hide the bar immediately
        setFlyoutVisible(flyout, false, !isFirstUpdate);
      } else if (isFirstUpdate) {
        // Page load with the main button out of view -> show without animating
        setFlyoutVisible(flyout, true, false);
      } else {
        // The main button left the viewport -> show again after a short delay
        showTimeout = window.setTimeout(() => setFlyoutVisible(flyout, true), 150);
      }

      isFirstUpdate = false;
    }
  };

  // The root margin can only be set on creation, so we have to recreate the
  // observer whenever the overlay at the top of the viewport changes.
  const createObserver = () => {
    const nextOcclusionHeight = getTopOcclusionHeight();

    if (observer && nextOcclusionHeight === occlusionHeight) {
      return;
    }

    occlusionHeight = nextOcclusionHeight;

    if (observer) {
      observer.disconnect();
    }

    observer = new IntersectionObserver(observerCallback, {
      threshold: 0,
      rootMargin: occlusionHeight > 0 ? `-${occlusionHeight}px 0px 0px 0px` : '0px',
    });

    observer.observe(observedElement);
  };

  let pendingRefresh;
  const scheduleCreateObserver = () => {
    window.cancelAnimationFrame(pendingRefresh);
    pendingRefresh = window.requestAnimationFrame(createObserver);
  };

  createObserver();
  watchTopOcclusion(scheduleCreateObserver);
}

/**
 * Keep the bar's quantity stepper in sync with the buy box amount input.
 * Clicking the flyout buttons forwards to the buy box controls so the existing
 * amount/tier-price handling (and its events) stays the single source of truth.
 *
 * @param {HTMLElement} flyout
 */
function initQuantitySync(flyout) {
  const amountInput = flyout.querySelector('.js-sticky-cart-flyout__amount-input');
  const minusButton = flyout.querySelector('.js-sticky-cart-flyout__amount-minus');
  const plusButton = flyout.querySelector('.js-sticky-cart-flyout__amount-plus');

  const buyBoxForm = document.querySelector(BUY_BOX_FORM_SELECTOR);
  const buyBoxInput = buyBoxForm?.elements?.['amnt'] || document.querySelector('.js-amount__input');

  if (!amountInput || !buyBoxInput) {
    return;
  }

  const getMin = () => parseInt(amountInput.min, 10) || 1;
  const getMax = () => parseInt(amountInput.max, 10) || Number.MAX_SAFE_INTEGER;
  const clamp = (value) => Math.min(Math.max(value, getMin()), getMax());

  const updateButtonStates = () => {
    const value = parseInt(amountInput.value, 10) || getMin();

    if (minusButton) {
      minusButton.disabled = value <= getMin();
    }

    if (plusButton) {
      plusButton.disabled = value >= getMax();
    }
  };

  const syncFromBuyBox = () => {
    const value = parseInt(buyBoxInput.value, 10);

    if (!Number.isNaN(value)) {
      amountInput.value = value;
      amountInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    updateButtonStates();
  };

  const changeQuantity = (direction) => {
    const buyBoxMinus = buyBoxForm?.querySelector('.js-amount__btn--minus');
    const buyBoxPlus = buyBoxForm?.querySelector('.js-amount__btn--plus');

    if (direction < 0 && buyBoxMinus) {
      buyBoxMinus.click();
      return;
    }

    if (direction > 0 && buyBoxPlus) {
      buyBoxPlus.click();
      return;
    }

    // Fallback for if the buy box buttons could not be found, but shouldn't happen.
    buyBoxInput.value = clamp((parseInt(buyBoxInput.value, 10) || 1) + direction);
    buyBoxInput.dispatchEvent(new Event('input', { bubbles: true }));
    buyBoxInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  if (minusButton) {
    minusButton.addEventListener('click', () => changeQuantity(-1));
  }

  if (plusButton) {
    plusButton.addEventListener('click', () => changeQuantity(1));
  }

  amountInput.addEventListener('change', () => {
    buyBoxInput.value = clamp(parseInt(amountInput.value, 10) || 1);
    buyBoxInput.dispatchEvent(new Event('change', { bubbles: true }));
    syncFromBuyBox();
  });

  buyBoxInput.addEventListener('input', syncFromBuyBox);
  buyBoxInput.addEventListener('change', syncFromBuyBox);

  syncFromBuyBox();
}

/**
 * Update the displayed total savings for the selected quantity.
 *
 * Savings = (reference price - current unit price) x quantity. Tier/BMSM discounts
 * are already part of the current unit price, so they are included implicitly.
 *
 * @param {HTMLElement} flyout
 */
function initSavingsSync(flyout) {
  const savingsWrapper = flyout.querySelector('.js-sticky-cart-flyout__savings');
  const savingsValue = flyout.querySelector('.js-sticky-cart-flyout__savings-value');
  const priceElement = flyout.querySelector('.js-tier-price-display');
  const amountInput = flyout.querySelector('.js-sticky-cart-flyout__amount-input');
  const referencePrice = parseFloat(flyout.dataset.referencePrice);

  if (!savingsWrapper || !savingsValue || !priceElement) {
    return;
  }

  const updateSavings = () => {
    const unitPrice = parsePrice(priceElement.textContent);
    const fallbackPrice = parseFloat(flyout.dataset.unitPrice);
    const currentPrice = Number.isNaN(unitPrice) ? fallbackPrice : unitPrice;
    const quantity = parseInt(amountInput?.value, 10) || 1;

    if (Number.isNaN(referencePrice) || Number.isNaN(currentPrice) || referencePrice <= currentPrice) {
      savingsWrapper.hidden = true;
      return;
    }

    const savings = (referencePrice - currentPrice) * quantity;
    savingsValue.textContent = `${savings.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
    savingsWrapper.hidden = false;
  };

  // The unit price element is updated by tierPriceUpdater, so react to DOM changes
  new MutationObserver(updateSavings).observe(priceElement, {
    childList: true,
    characterData: true,
    subtree: true,
  });

  if (amountInput) {
    amountInput.addEventListener('input', updateSavings);
    amountInput.addEventListener('change', updateSavings);
  }

  updateSavings();
}

export default function stickyCartFlyout() {
  const flyout = document.querySelector(ROOT_SELECTOR);

  if (!flyout) {
    return;
  }

  initVisibilityObserver(flyout);
  initQuantitySync(flyout);
  initSavingsSync(flyout);

  // Make sure the bar's own add-to-cart button is bound to the global handler
  if (typeof window.addToBasketFunctions === 'function') {
    window.addToBasketFunctions();
  }
}
